// The Forecasting tab's over- and understaffed hours (Stage 3, moved out of
// ReportingPage on 2026-10-26 so the Phone & Chat view and the earlier layout
// show the same panel): rows are open hours, columns Sunday-Saturday, each open
// cell shows its spare agents (staffed in-queue time minus the phone and chat
// workload). Short is the warm pole (red tint), over the cool one (blue tint),
// ok carries none, and the number is always shown, so colour is never the only
// signal. Hover or keyboard focus gives the staffed/workload breakdown. One
// report per period, fetched the first time that period is shown.
import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography, useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { fetchForecastHeatmap } from '../api.js'
import SectionHeading from './SectionHeading.jsx'
import {
  heatmapStatusToken, heatmapCellIndex, heatmapHours, heatmapHourLabel, formatSpare, heatmapSummaryText,
  heatmapTooltipLines, HEATMAP_STATUS_LABEL,
} from '../forecastHeatmap.js'

const CELL_BORDER = { border: '1px solid', borderColor: 'divider' }

function ForecastHeatmapGrid({ report }) {
  const theme = useTheme()
  const index = heatmapCellIndex(report)
  const hours = heatmapHours(report)
  const tint = (status) => {
    const token = heatmapStatusToken(status)
    return token ? alpha(theme.palette[token].main, theme.palette.mode === 'dark' ? 0.32 : 0.2) : undefined
  }
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', width: 'fit-content', maxWidth: '100%' }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={CELL_BORDER}>Hour</TableCell>
            {report.weekdays.map((d) => (
              <TableCell key={d} align="center" sx={{ ...CELL_BORDER, width: 72 }}>{d}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {hours.map((hour) => (
            <TableRow key={hour}>
              <TableCell sx={CELL_BORDER}>{heatmapHourLabel(hour)}</TableCell>
              {report.weekdays.map((d) => {
                const cell = index.get(`${d}|${hour}`)
                const lines = heatmapTooltipLines(cell)
                const judged = cell?.open && cell.status
                return (
                  <Tooltip
                    key={d}
                    arrow
                    title={<Box>{lines.map((l, i) => <Box key={i}>{l}</Box>)}</Box>}
                  >
                    <TableCell
                      align="center"
                      tabIndex={0}
                      sx={{
                        ...CELL_BORDER,
                        bgcolor: tint(cell?.status),
                        color: judged ? undefined : 'text.disabled',
                        fontVariantNumeric: 'tabular-nums',
                        fontWeight: cell?.status === 'ok' || !judged ? 400 : 600,
                      }}
                    >
                      {judged ? formatSpare(cell.spareFte) : '—'}
                    </TableCell>
                  </Tooltip>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

export default function ForecastHeatmapPanel() {
  const [period, setPeriod] = useState('past')
  const [reports, setReports] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const report = reports[period]

  useEffect(() => {
    if (reports[period]) return undefined
    let cancelled = false
    setLoading(true)
    setError(null)
    fetchForecastHeatmap(period)
      .then((r) => { if (!cancelled) setReports((all) => ({ ...all, [period]: r })) })
      .catch((e) => { if (!cancelled) setError(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [period, reports])

  return (
    <Box>
      <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="Over- and understaffed hours" descriptionMaxWidth={640}>
        Spare agents in each hour of the week: staffed in-queue time minus the phone and chat workload, chat never
        below one agent in an open hour. Short is under {report?.thresholds?.short ?? 2} spare (red), over is more
        than {report?.thresholds?.over ?? 4} (blue); an hour counts as open when phone calls reach the queue in it.
        Last 8 weeks is what happened; Next 4 weeks is the coming schedule against a typical recent week's hourly
        demand, scaled to the forecast. — means closed or not scheduled yet. Nick Email time counts up to the chat
        workload, and includes one rep who takes no chat.
      </SectionHeading>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={period}
        onChange={(_e, v) => { if (v) setPeriod(v) }}
        sx={{ mb: 1 }}
      >
        <ToggleButton value="past">Last 8 weeks</ToggleButton>
        <ToggleButton value="next">Next 4 weeks</ToggleButton>
      </ToggleButtonGroup>
      {loading && !report ? (
        <Box><CircularProgress size={20} /></Box>
      ) : error && !report ? (
        <Alert severity="error">{error}</Alert>
      ) : report ? (
        <>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {report.coverageFrom} to {report.coverageTo}. {heatmapSummaryText(report)}
          </Typography>
          <ForecastHeatmapGrid report={report} />
          <Stack direction="row" spacing={2} sx={{ mt: 1 }} alignItems="center">
            {['short', 'ok', 'over'].map((status) => (
              <Stack key={status} direction="row" spacing={0.75} alignItems="center">
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: '2px',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: heatmapStatusToken(status)
                      ? (t) => alpha(t.palette[heatmapStatusToken(status)].main, t.palette.mode === 'dark' ? 0.32 : 0.2)
                      : undefined,
                  }}
                />
                <Typography variant="caption" color="text.secondary">{HEATMAP_STATUS_LABEL[status]}</Typography>
              </Stack>
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  )
}
